import type { ModuleName } from '@contexts/modules-provider';

import {
  CobaltInlineDropdown,
  CobaltInlineDropdownItem,
} from '@cae/cobalt-react/components/inline-dropdown';
import { classNames } from '@cae/cobalt-react/utils';
import * as React from 'react';

import type { ModuleSwitcherVariantProps } from '../../ModuleSwitcher.types';

import { MODULE_ILLUSTRATIONS } from '../../ModuleSwitcher.utils';
import styles from './ModuleSwitcherExpanded.module.css';

const ModuleSwitcherExpanded = React.memo(
  ({
    selectedModule,
    modules,
    hasMultipleModules,
    onModuleSelect,
    onModuleSwitcherToggle,
  }: Readonly<ModuleSwitcherVariantProps>): React.ReactElement => {
    const Illustration = MODULE_ILLUSTRATIONS[selectedModule.name];

    // Tracks whether the current click originated from an option selection so the
    // root div onClick can skip firing the toggle event on that same click.
    const selectionHappenedRef = React.useRef(false);

    const handleSelectionChange = React.useCallback(
      (moduleName: ModuleName): void => {
        selectionHappenedRef.current = true;
        void onModuleSelect(moduleName);
      },
      [onModuleSelect]
    );

    const handleDropdownClick = React.useCallback((): void => {
      if (selectionHappenedRef.current) {
        selectionHappenedRef.current = false;
        return;
      }
      onModuleSwitcherToggle();
    }, [onModuleSwitcherToggle]);

    const moduleSwitcherClasses = classNames(
      styles.moduleSwitcher,
      !hasMultipleModules && styles.singleModule
    );

    return (
      <div className={moduleSwitcherClasses} data-testid="module-switcher">
        <div className={styles.illustration} aria-hidden="true">
          <Illustration />
        </div>

        {hasMultipleModules ? (
          <div className={styles.dropdown}>
            <CobaltInlineDropdown<ModuleName>
              value={selectedModule.name}
              onSelectionChange={handleSelectionChange}
              onClick={handleDropdownClick}
              size="small"
              variant="header"
              aria-label="Switch module"
            >
              {modules.map(module => (
                <CobaltInlineDropdownItem<ModuleName>
                  key={module.name}
                  value={module.name}
                >
                  {module.name}
                </CobaltInlineDropdownItem>
              ))}
            </CobaltInlineDropdown>
          </div>
        ) : (
          <span className={`${styles.moduleName} body-02`}>
            {selectedModule.name}
          </span>
        )}
      </div>
    );
  }
);

ModuleSwitcherExpanded.displayName = 'ModuleSwitcherExpanded';

export { ModuleSwitcherExpanded };
